import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { TNPSC_UNIT2_QUESTIONS } from "../data/tnpscUnit2Questions";

// ─────────────────────────────────────────────────────────────────────────────
// TNPSC Code 554 — Unit II: Data Structures and Algorithms.
// Content drafted from the official syllabus topic list; each concept has an
// explanation plus a pseudocode/code example. NOT YET human-reviewed by a
// subject-matter expert — treat as a first draft, verify before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Linear Structures", "Trees & Heaps", "Sorting & Searching", "Hashing", "Graph Algorithms", "Algorithm Analysis"];

const SECTION_COLORS = {
  "Linear Structures":   "#2563eb",
  "Trees & Heaps":       "#16a34a",
  "Sorting & Searching":  "#d97706",
  "Hashing":              "#dc2626",
  "Graph Algorithms":     "#7c3aed",
  "Algorithm Analysis":   "#0891b2",
};

const CONCEPTS = [
  // ── Linear Structures ──────────────────────────────────────────────────
  { section: "Linear Structures", title: "List ADT: array vs. linked implementation",
    explanation: "An array-based list gives O(1) random access but O(n) insert/delete in the middle (elements must shift), and its size is fixed or must be reallocated. A linked list gives O(1) insert/delete once you're at the right node, but O(n) access by index since you must walk from the head. TNPSC questions here usually test exactly this trade-off table.",
    example: `// Array-based insert at front: O(n) — must shift everything right
for (i = n; i > 0; i--) arr[i] = arr[i-1];
arr[0] = newValue;

// Linked-list insert at front: O(1) — just repoint the head
newNode->next = head;
head = newNode;` },
  { section: "Linear Structures", title: "Singly, circularly, and doubly linked lists",
    explanation: "A singly linked list has one 'next' pointer per node and ends in NULL. A circular linked list's last node points back to the first, so traversal never hits NULL — useful for round-robin scheduling. A doubly linked list adds a 'prev' pointer, allowing O(1) backward traversal and O(1) deletion given only a pointer to the node.",
    example: `struct Node  { int data; Node *next; };              // singly
struct DNode { int data; DNode *prev, *next; };       // doubly

// Circular: last node's next points back to head, not NULL
tail->next = head;` },
  { section: "Linear Structures", title: "Polynomial representation and manipulation using linked lists",
    explanation: "A polynomial is stored as a linked list of nodes, each holding (coefficient, exponent), sorted by descending exponent, with zero-coefficient terms omitted. Addition/subtraction merges the two lists exponent-by-exponent, like a merge step in merge sort — O(m+n) total for lists of length m and n.",
    example: `struct Term { int coeff, exp; Term *next; };

// 3x^2 + 5  add  2x^2 + 4x
// walk both lists together:
//   x^2 terms combine -> 5x^2
//   4x  (no match)     -> copied as-is
//   5   (no match)     -> copied as-is
// result: 5x^2 + 4x + 5` },
  { section: "Linear Structures", title: "Stack ADT and expression evaluation",
    explanation: "A stack is LIFO (last in, first out) with O(1) push/pop, implementable on an array or a linked list. Its classic TNPSC application is expression handling: converting infix to postfix using an operator stack, then evaluating postfix by pushing operands and, on each operator, popping two operands and pushing the result.",
    example: `// Postfix '5 3 4 * +' evaluation
push(5); push(3); push(4);
// see '*': pop 4,3 -> push 3*4=12   stack: [5, 12]
// see '+': pop 12,5 -> push 5+12=17
// result = 17` },
  { section: "Linear Structures", title: "Queue ADT: circular queue, deque, priority queue",
    explanation: "A queue is FIFO; a naive array queue wastes space after dequeues, which a circular queue fixes by wrapping the front/rear indices modulo capacity. A double-ended queue (deque) allows insertion/deletion at BOTH ends in O(1). A priority queue serves the highest (or lowest) priority element first, typically via a binary heap.",
    example: `// Circular queue — wrap indices with modulo capacity
rear  = (rear + 1) % capacity;
front = (front + 1) % capacity;

// CPU job scheduling by priority  -> priority queue (binary heap)
// Browser back/forward history    -> deque
// Print spooler, submitted order  -> plain circular queue` },

  // ── Trees & Heaps ────────────────────────────────────────────────────────
  { section: "Trees & Heaps", title: "Binary Trees, Binary Search Trees, and traversals",
    explanation: "A binary tree has at most 2 children per node. A Binary Search Tree (BST) orders nodes so left-subtree < node < right-subtree, giving O(h) search/insert/delete (O(log n) if balanced, O(n) if it degenerates into a chain). Inorder traversal of a BST visits nodes in sorted order; preorder clones/serializes; postorder safely deletes bottom-up; level order (BFS) visits by depth.",
    example: `void inorder(Node* root) {
    if (!root) return;
    inorder(root->left);
    print(root->data);
    inorder(root->right);
}
// Inserting 5,3,8,1,4 then INORDER traversal prints: 1, 3, 4, 5, 8` },
  { section: "Trees & Heaps", title: "AVL Trees: self-balancing via rotations",
    explanation: "An AVL tree is a BST where every node's balance factor (height of left subtree − height of right subtree) stays in {−1, 0, +1}, guaranteeing O(log n) height always. A violation after insert/delete is fixed with a rotation: LL and RR cases need one single rotation, LR and RL cases need a double rotation.",
    example: `// Inserting 10, 20, 30 (increasing order) into an empty AVL tree
// causes an RR imbalance at node 10 —
// fixed by a single LEFT rotation:
//   before:  10 -> 20 -> 30 (a chain)
//   after:   20 is the new root, with 10 and 30 as children` },
  { section: "Trees & Heaps", title: "Splay Trees: self-adjusting by splaying",
    explanation: "A splay tree has no strict balance invariant; instead, every access (search/insert/delete) 'splays' the accessed node up to the root via a sequence of rotations (zig, zig-zig, zig-zag). This gives amortized O(log n) per operation, and recently/frequently accessed items end up near the root — fast for locality-heavy access patterns like caches.",
    example: `// Searching for the same key twice in a row:
// 1st search: splays the node to the root (up to O(log n) rotations)
// 2nd search: O(1) — the node is already the root` },
  { section: "Trees & Heaps", title: "Red-Black Trees: balance via node colouring",
    explanation: "A Red-Black tree is a BST where every node is coloured red or black, following properties (root is black; red nodes can't have red children; every root-to-NULL path has the same number of black nodes) that bound height at O(log n). It allows looser balance than AVL (more color-only fixups, fewer rotations), which is why it's used inside C++ STL's map/set and Java's TreeMap.",
    example: `// 3 key properties that bound height at O(log n):
// 1. The root is always black
// 2. A red node cannot have a red child
// 3. Every root-to-NULL path has the same number of black nodes
//
// AVL: more strictly balanced, faster lookups, more rotations on write
// Red-Black: looser balance, cheaper rebalancing on frequent insert/delete` },
  { section: "Trees & Heaps", title: "Heap variants: Binary, Skew, Leftist, Binomial, Fibonacci",
    explanation: "All are priority-queue implementations with different cost trade-offs. Binary heap: O(log n) insert/extract-min, O(1) find-min, but O(n) merge. Leftist and Skew heaps: O(log n) merge — their real advantage. Binomial heap: O(log n) insert/extract-min/merge. Fibonacci heap: O(1) amortized insert and decrease-key, O(log n) amortized extract-min — the structure that makes Dijkstra's algorithm run in O(E + V log V).",
    example: `// Binary heap:    O(log n) insert/extract-min, O(n) merge
// Leftist/Skew:    O(log n) MERGE (their advantage over binary heaps)
// Fibonacci heap:  O(1) amortized insert/decrease-key
//   -> Dijkstra with a Fibonacci heap: O(E + V log V)
//      vs. a binary heap:              O((E+V) log V)` },

  // ── Sorting & Searching ────────────────────────────────────────────────
  { section: "Sorting & Searching", title: "Sorting algorithms: complexity and stability",
    explanation: "A high-yield TNPSC table to memorize exactly. Insertion/Selection/Bubble sort: O(n²) worst and average, O(1) extra space; insertion and bubble are stable, selection is not. Shell sort: sub-quadratic, in-place, not stable. Quick sort: O(n log n) average but O(n²) worst case, in-place, not stable. Merge sort: O(n log n) worst case always, O(n) extra space, IS stable. Heap sort: O(n log n) worst case, O(1) extra space, NOT stable. Radix sort: O(d·(n+k)) — not comparison-based.",
    example: `// Sort   | Worst      | Space  | Stable?
// Insertion/Bubble | O(n^2) | O(1)  | Yes
// Selection         | O(n^2) | O(1)  | No
// Quick sort         | O(n^2) | O(log n) | No
// Merge sort         | O(n log n) | O(n) | Yes
// Heap sort           | O(n log n) | O(1) | No` },
  { section: "Sorting & Searching", title: "Searching: Linear vs. Binary search",
    explanation: "Linear search checks every element in order: O(n), works on unsorted data. Binary search repeatedly halves the search range by comparing the middle element: O(log n), but REQUIRES the data to already be sorted — a classic trap is asking for binary search's complexity on unsorted input (it simply doesn't work correctly, not just slowly).",
    example: `int binarySearch(int a[], int n, int key) {
    int l = 0, r = n - 1;
    while (l <= r) {
        int mid = (l + r) / 2;
        if (a[mid] == key) return mid;
        if (key < a[mid]) r = mid - 1;
        else l = mid + 1;
    }
    return -1;   // a[] must already be sorted
}` },

  // ── Hashing ─────────────────────────────────────────────────────────────
  { section: "Hashing", title: "Hashing: functions, collisions, and resolution strategies",
    explanation: "A hash function maps a key to an array index; collisions are unavoidable once keys outnumber a fixed table size. Separate chaining resolves this by storing a linked list at each index. Open addressing probes for the next free slot (linear, quadratic, or double hashing) — needs the load factor well under 1. Rehashing rebuilds the table at a larger size once the load factor crosses a threshold. Extendible hashing is a dynamic, disk-friendly scheme using a directory of pointers that doubles the directory (not the whole table) on overflow.",
    example: `// Separate chaining — each slot holds a linked list of colliding keys
table[hash(key) % size].push_back(key);

// Open addressing — linear probing
index = hash(key) % size;
while (table[index] occupied)
    index = (index + 1) % size;
// linear probing suffers "primary clustering";
// double hashing gives the most uniform probe distribution` },

  // ── Graph Algorithms ────────────────────────────────────────────────────
  { section: "Graph Algorithms", title: "Graph traversals: BFS and DFS",
    explanation: "Breadth-First Search visits a graph level by level using a QUEUE, and finds the shortest path (fewest edges) from a source in an UNWEIGHTED graph. Depth-First Search goes as deep as possible before backtracking, using a STACK (or recursion), and is the basis for topological sort, cycle detection, and connected components. Both are O(V + E) with an adjacency list.",
    example: `// BFS — queue, shortest path in an UNWEIGHTED graph
queue.push(source);
while (!queue.empty()) {
    v = queue.pop();
    for (u : neighbors(v)) if (!visited[u]) queue.push(u);
}
// DFS — stack/recursion -> topological sort, cycle detection` },
  { section: "Graph Algorithms", title: "Minimum Spanning Tree: Prim's and Kruskal's algorithms",
    explanation: "An MST connects all vertices of a weighted, connected, undirected graph with the minimum total edge weight and no cycles. Kruskal's algorithm sorts ALL edges by weight and greedily adds each one if it doesn't form a cycle (via Union-Find) — O(E log E). Prim's algorithm grows a single tree from a start vertex, repeatedly adding the cheapest connecting edge — O(E log V) with a binary heap. Both are greedy and both are provably correct.",
    example: `// Kruskal's: sort ALL edges, greedily add if no cycle forms
sort(edges by weight);
for (edge : edges)
    if (find(edge.u) != find(edge.v)) { union(edge.u, edge.v); addToMST(edge); }

// Prim's: grow ONE tree, always add the cheapest connecting edge` },
  { section: "Graph Algorithms", title: "Shortest path: Dijkstra's algorithm",
    explanation: "Dijkstra's algorithm finds shortest paths from a single source to all other vertices in a weighted graph with NON-NEGATIVE edge weights, greedily picking the unvisited vertex with the smallest known distance and relaxing its edges. With a binary heap: O((V+E) log V). It FAILS with negative edge weights — that's exactly when Bellman-Ford is needed instead.",
    example: `dist[source] = 0;
while (unvisited vertices remain) {
    u = unvisited vertex with smallest dist[u];
    for (v : neighbors(u))
        if (dist[u] + weight(u,v) < dist[v])
            dist[v] = dist[u] + weight(u,v);
}
// Fails on negative edges — use Bellman-Ford instead` },
  { section: "Graph Algorithms", title: "DAGs, topological ordering, and All-Pairs Shortest Path",
    explanation: "A Directed Acyclic Graph (DAG) has directed edges and no cycles. A topological order lists vertices so every edge u→v has u before v — only possible for a DAG, computed via DFS finish-order (reversed) or Kahn's algorithm. For ALL pairs' shortest paths at once, Floyd-Warshall runs in O(V³) using dynamic programming, works with negative edges (but not negative cycles).",
    example: `// Floyd-Warshall core recurrence — worth memorizing verbatim
for (k = 0; k < V; k++)
  for (i = 0; i < V; i++)
    for (j = 0; j < V; j++)
      dist[i][j] = min(dist[i][j], dist[i][k] + dist[k][j]);` },
  { section: "Graph Algorithms", title: "Bellman-Ford algorithm",
    explanation: "Bellman-Ford finds single-source shortest paths and, unlike Dijkstra, correctly handles NEGATIVE edge weights. It relaxes every edge V−1 times (dynamic-programming style); a further V-th pass that still finds a shorter distance means a negative-weight cycle exists. Complexity: O(V·E), slower than Dijkstra's O((V+E) log V) — the price paid for handling negative weights.",
    example: `for (i = 1; i < V; i++)
    for (edge (u,v,w) : edges)
        if (dist[u] + w < dist[v]) dist[v] = dist[u] + w;

// one more pass that STILL improves a distance
// => a negative-weight cycle exists` },
  { section: "Graph Algorithms", title: "Network flow: Ford-Fulkerson algorithm",
    explanation: "The Ford-Fulkerson method finds the maximum flow from a source to a sink in a flow network by repeatedly finding an 'augmenting path' (spare capacity, found via BFS or DFS) in the residual graph and pushing flow along it, until none remain. By the max-flow min-cut theorem, the resulting maximum flow equals the capacity of the minimum cut separating source from sink.",
    example: `// Repeatedly:
//   1. Find an augmenting path source -> sink with spare capacity
//   2. Push flow equal to the path's bottleneck capacity
//   3. Update the residual graph
// until no augmenting path remains.
// Using BFS specifically = Edmonds-Karp, guaranteed O(V * E^2)` },

  // ── Algorithm Analysis ──────────────────────────────────────────────────
  { section: "Algorithm Analysis", title: "Amortized analysis of algorithms",
    explanation: "Amortized analysis bounds the AVERAGE cost per operation over a worst-case SEQUENCE of operations, even when individual operations occasionally cost much more. Three standard techniques: the aggregate method, the accounting method (some operations overpay to build credit), and the potential method (a potential function Φ; amortized cost = actual cost + change in Φ).",
    example: `// Classic example: a dynamic array that DOUBLES when full
// A single insert that triggers doubling costs O(n)
// but doubling happens rarely (geometrically less often)
// -> AMORTIZED cost per insert averages out to O(1)` },
  { section: "Algorithm Analysis", title: "Asymptotic analysis: Big-O, Omega, Theta",
    explanation: "Big-O (O) is an upper bound on growth rate (worst case), Big-Omega (Ω) is a lower bound (best case), and Big-Theta (Θ) is a tight bound (both upper and lower). These describe how running time or space scales with input size n as n → ∞, ignoring constant factors and lower-order terms.",
    example: `// Binary search:
//   O(log n)  — upper bound / worst case
//   Ω(1)      — best case (target found on the very first check)
// best != worst here, so Θ(log n) would NOT correctly
// describe binary search's best case, only its worst/typical case` },
  { section: "Algorithm Analysis", title: "Solving recurrence equations (Master Theorem)",
    explanation: "Divide-and-conquer algorithms' running time is often expressed as T(n) = a·T(n/b) + f(n). The Master Theorem gives a direct answer by comparing f(n) to n^(log_b a): if f(n) is polynomially smaller, T(n) = Θ(n^(log_b a)); if the same order, T(n) = Θ(n^(log_b a) · log n); if polynomially larger (with a regularity condition), T(n) = Θ(f(n)).",
    example: `// Merge sort: T(n) = 2T(n/2) + O(n)
// a=2, b=2, f(n)=n
// n^(log_2 2) = n^1  — matches f(n)'s order
// -> Master Theorem case 2 -> T(n) = Theta(n log n)` },
  { section: "Algorithm Analysis", title: "Algorithm design techniques: Greedy, Dynamic Programming, Divide and Conquer",
    explanation: "Greedy algorithms make the locally-best choice at each step and never reconsider — fast, but only optimal for problems with the 'greedy-choice property' (e.g. Prim's/Kruskal's MST, Huffman coding). Dynamic Programming solves problems with overlapping subproblems and optimal substructure by solving each ONCE and reusing the result (e.g. 0/1 knapsack, longest common subsequence). Divide and Conquer splits a problem into INDEPENDENT subproblems that don't overlap, solves each recursively, then combines (e.g. merge sort, quick sort, binary search).",
    example: `// 0/1 Knapsack CANNOT be solved optimally by a greedy approach,
// but Fractional Knapsack CAN —
// Fractional Knapsack has the greedy-choice property
// (take items by best value/weight ratio);
// 0/1 Knapsack does not, since whole-item constraints
// can force a worse combination than the greedy pick.` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
};

function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function TnpscUnit2Page() {
  const [mode, setMode] = useState("guide");
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  const filtered = useMemo(() => (
    activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection)
  ), [activeSection]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? TNPSC_UNIT2_QUESTIONS : TNPSC_UNIT2_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => { setMode(m); setScore({ correct: 0, answered: 0 }); };
  const handleAnswer = (isCorrect) => setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));

  return (
    <div style={S.page}>
      <Link to="/tnpsc" style={S.back}>← Back to TNPSC 554 Menu</Link>
      {" · "}
      <Link to="/tnpsc-unit1" style={S.back}>← Unit I</Link>
      {" · "}
      <Link to="/tnpsc-unit3" style={S.back}>Unit III →</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🎓 TNPSC 554 — Unit II</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Data Structures and Algorithms</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          20 questions in the exam · {CONCEPTS.length} concepts explained · {TNPSC_UNIT2_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify before exam use
        </div>
      </div>

      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({TNPSC_UNIT2_QUESTIONS.length})</button>
      </div>

      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a href={`#concept-${i}`} style={{ color:"#e2e8f0", textDecoration:"none" }}>{c.title}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filtered.map((c, i) => (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / SCRIPT</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
      ))}
      </>
      )}
    </div>
  );
}
